import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {InfoLine} from '@dropbox/dig-icons/assets';
import {WorkstreamMetricVisibility} from 'client';
import {Link} from 'components/DSYS/Link';
import {MnpiMetricIndicator} from 'components/DSYS/MnpiMetricIndicator';
import {ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import type {ReactNode} from 'react';

import type {ScorecardMetric} from './scorecardData';
import styles from './ScorecardsTable.module.css';
import {ScorecardWorkstreamStatusBadge} from './ScorecardWorkstreamStatusBadge';

type ScorecardMetricNameCellProps = {
  metric: ScorecardMetric;
  disclosure?: ReactNode;
  snapshotKey?: string | null;
  fallbackWorkstreamId?: number;
  fallbackWorkstreamName?: string;
};

const WORKSTREAM_SUBTITLE_STYLE = {
  maxWidth: 330,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

export const scorecardWorkstreamPath = (workstreamId?: number): string | null =>
  workstreamId ? ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(workstreamId)) : null;

const parseWorkstreamId = (value?: string): number | undefined => {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const ScorecardMetricNameCell = ({
  metric,
  disclosure,
  snapshotKey,
  fallbackWorkstreamId,
  fallbackWorkstreamName,
}: ScorecardMetricNameCellProps) => {
  const workstreamName =
    metric.sourceWorkstreamName?.trim() ||
    metric.groupLabel?.trim() ||
    metric.sectionLabel?.trim() ||
    fallbackWorkstreamName?.trim() ||
    undefined;
  const workstreamPath = scorecardWorkstreamPath(
    metric.sourceWorkstreamId ??
      fallbackWorkstreamId ??
      parseWorkstreamId(metric.groupId) ??
      parseWorkstreamId(metric.sectionId)
  );

  // Keep the toggletip glued to the last word so it never wraps onto its own line:
  // the trailing word + icon share a nowrap unit that wraps together.
  const trimmedName = metric.name.trim();
  const lastSpaceIndex = trimmedName.lastIndexOf(' ');
  const nameHead = lastSpaceIndex === -1 ? '' : trimmedName.slice(0, lastSpaceIndex + 1);
  const nameTail = lastSpaceIndex === -1 ? trimmedName : trimmedName.slice(lastSpaceIndex + 1);
  const metricMnpiIndicator = metric.isMnpi ? (
    <MnpiMetricIndicator
      className={`${atoms({marginRight: '4'})} ${styles.scorecardMetricMnpiIcon}`}
      tooltip={
        metric.visibility === WorkstreamMetricVisibility.REDACTED
          ? t('redacted_metric_tooltip').toString()
          : undefined
      }
    />
  ) : null;

  return (
    <Box display="flex" alignItems="flex-start" style={{gap: 8, minWidth: 0}}>
      {disclosure ? (
        <Box display="inline-flex" alignItems="center" className={styles.metricDisclosureIcon}>
          {disclosure}
        </Box>
      ) : null}
      <Stack gap="2" style={{minWidth: 0}}>
        {workstreamName ? (
          <Box display="flex" alignItems="center" className={atoms({columnGap: '4'})}>
            <ScorecardWorkstreamStatusBadge
              context={metric.sourceWorkstreamContext}
              snapshotKey={snapshotKey}
            />
            <Text
              tagName="div"
              size="xsmall"
              color="subtle"
              isBold
              className={styles.metricWorkstreamSubtitle}
              style={WORKSTREAM_SUBTITLE_STYLE}
              title={workstreamName}
            >
              {workstreamPath ? (
                <Link to={workstreamPath} monochromatic hasNoUnderline showUnderlineOnHover>
                  {workstreamName}
                </Link>
              ) : (
                workstreamName
              )}
            </Text>
          </Box>
        ) : null}
        <Text tagName="div" size="small" isBold>
          {metricMnpiIndicator}
          {metric.description ? (
            <>
              {nameHead}
              <Box tagName="span" display="inline" style={{whiteSpace: 'nowrap'}}>
                {nameTail}
                <Box
                  tagName="span"
                  display="inline-flex"
                  marginLeft="2"
                  className={styles.metricInfoTooltip}
                  style={{verticalAlign: 'middle', transform: 'translateY(-1px)'}}
                >
                  <Tooltip title={metric.description} placement="top">
                    <IconButton
                      variant="borderless"
                      size="small"
                      aria-label={t('scorecard_metric_description_label')}
                    >
                      <UIIcon src={InfoLine} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </>
          ) : (
            metric.name
          )}
        </Text>
      </Stack>
    </Box>
  );
};
